import React, { useEffect, useState } from "react";
import { db } from "../../firebase";
import { collection, addDoc, deleteDoc, doc, getDocs, query, where, Timestamp } from "firebase/firestore";
import { useActiveBar } from "../ActiveBarContext";

export default function Jobs() {
  const { activeBar } = useActiveBar();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [payRange, setPayRange] = useState("");
  const [contactMethod, setContactMethod] = useState("");

  const loadJobs = async () => {
    if (!activeBar) {
      setJobs([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    const q = query(collection(db, "jobs"), where("barId", "==", activeBar.id));
    try {
      const snap = await getDocs(q);
      setJobs(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    } catch (err) {
      console.error(err);
    }
    setLoading(false);
  };

  useEffect(() => {
    loadJobs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeBar]);

  // Bar city/state come from splitting the stored address string, since
  // that's the only location data most bars have — same approach the
  // Admin Overview page uses for its "Bars by City" breakdown.
  const guessCityState = (address) => {
    const parts = (address || "").split(",").map((p) => p.trim());
    return {
      city: parts.length >= 2 ? parts[parts.length - 3] || "" : "",
      state: parts.length >= 2 ? (parts[parts.length - 2] || "").split(" ")[0] : "",
    };
  };

  const postJob = async () => {
    if (!activeBar || !title.trim() || !contactMethod.trim()) return;

    const { city, state } = guessCityState(activeBar.address);

    const jobData = {
      barId: activeBar.id,
      barName: activeBar.name || "Unnamed Bar",
      address: activeBar.address || "",
      city,
      state,
      lat: activeBar.lat || null,
      lng: activeBar.lng || null,
      title: title.trim(),
      description: description.trim(),
      payRange: payRange.trim(),
      contactMethod: contactMethod.trim(),
      postedAt: Timestamp.now(),
    };

    try {
      const docRef = await addDoc(collection(db, "jobs"), jobData);
      setJobs((prev) => [...prev, { id: docRef.id, ...jobData }]);
      setTitle("");
      setDescription("");
      setPayRange("");
      setContactMethod("");
    } catch (err) {
      console.error(err);
    }
  };

  const removeJob = async (jobId) => {
    if (!window.confirm("Remove this job posting?")) return;
    try {
      await deleteDoc(doc(db, "jobs", jobId));
      setJobs((prev) => prev.filter((j) => j.id !== jobId));
    } catch (err) {
      console.error(err);
    }
  };

  const inputStyle = {
    width: "100%",
    padding: "12px",
    borderRadius: "8px",
    border: "none",
    fontSize: "15px",
    boxSizing: "border-box",
  };
  const labelStyle = { display: "block", marginBottom: "8px", fontWeight: 600 };

  if (!activeBar) {
    return <p style={{ color: "#fff" }}>Claim a bar first before posting job openings.</p>;
  }

  return (
    <div style={{ color: "#fff" }}>
      <h2 style={{ marginBottom: "6px" }}>Job Postings</h2>
      <p style={{ marginBottom: "20px", color: "#B8B0C8" }}>
        Post open positions at {activeBar.name || "your bar"}. Job seekers can find these in the app,
        starting with bars nearby.
      </p>

      <div style={{ background: "#0D0B1A", padding: "24px", borderRadius: "12px", maxWidth: "560px", marginBottom: "30px" }}>
        <label style={labelStyle}>Position Title</label>
        <input
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Bartender, Line Cook, Door Staff..."
          style={{ ...inputStyle, marginBottom: "16px" }}
        />

        <label style={labelStyle}>Description</label>
        <textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="What the role involves, hours, experience needed..."
          style={{ ...inputStyle, marginBottom: "16px", height: "90px" }}
        />

        <label style={labelStyle}>Pay Range (optional)</label>
        <input
          type="text"
          value={payRange}
          onChange={(e) => setPayRange(e.target.value)}
          placeholder="$15-18/hr + tips"
          style={{ ...inputStyle, marginBottom: "16px" }}
        />

        <label style={labelStyle}>How should applicants reach you?</label>
        <input
          type="text"
          value={contactMethod}
          onChange={(e) => setContactMethod(e.target.value)}
          placeholder="Call (555) 123-4567 or email us@yourbar.com"
          style={{ ...inputStyle, marginBottom: "20px" }}
        />

        <button
          onClick={postJob}
          style={{
            background: "linear-gradient(90deg, #6D28D9, #A855F7)",
            border: "none",
            padding: "10px 18px",
            borderRadius: "8px",
            color: "#fff",
            fontWeight: 600,
            cursor: "pointer",
          }}
        >
          Post Job
        </button>
      </div>

      <h3 style={{ marginBottom: "10px" }}>Current Postings</h3>
      {loading ? (
        <p style={{ color: "#B8B0C8" }}>Loading...</p>
      ) : jobs.length === 0 ? (
        <p style={{ color: "#B8B0C8" }}>No open positions posted yet.</p>
      ) : (
        jobs.map((job) => (
          <div
            key={job.id}
            style={{
              background: "#0D0B1A",
              border: "1px solid rgba(255,255,255,0.08)",
              padding: "15px",
              borderRadius: "10px",
              marginBottom: "12px",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
            }}
          >
            <div>
              <h4 style={{ margin: 0, fontSize: "18px" }}>{job.title}</h4>
              {job.payRange && <p style={{ margin: "4px 0 0", color: "#A855F7", fontSize: "14px" }}>{job.payRange}</p>}
              {job.description && <p style={{ margin: "6px 0 0", color: "#B8B0C8", fontSize: "14px" }}>{job.description}</p>}
              <p style={{ margin: "6px 0 0", color: "#8A84A0", fontSize: "16px" }}>{job.contactMethod}</p>
            </div>
            <button
              onClick={() => removeJob(job.id)}
              style={{ background: "transparent", border: "none", color: "#F472B6", cursor: "pointer", fontSize: "16px", whiteSpace: "nowrap" }}
            >
              Remove
            </button>
          </div>
        ))
      )}
    </div>
  );
}